import { Button, CircularProgress, Dialog, DialogActions, DialogContent, List, ListItem, ListItemText, TextField } from "@mui/material";
import { get, getDatabase, ref } from "firebase/database";
import React from "react";
import { getArchiveFile } from "utils/ArchiveReader";
import { ArchiveType } from "utils/archiveType";

function haversine(lat1: number, lng1: number, lat2: number, lng2: number) {
    const toRad = (v: number) => (v * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const closeKPCount = (kpdata: any, latNum: number, lngNum: number, dst: number) => {
    let count = 0;
    Object.values(kpdata).forEach((kp: any) => {
        if (kp.loc && typeof kp.loc.lat === "number" && typeof kp.loc.lng === "number") {
            const d = haversine(latNum, lngNum, kp.loc.lat, kp.loc.lng);
            if (d <= dst) count++;
        }
    });
    return count;
}

type KPDataType = ArchiveType['eventsdata']['kpdata'];

export const FindEventsByLoc: React.FC = () => {
    const [open, setOpen] = React.useState(false);
    const [lat, setLat] = React.useState("");
    const [lng, setLng] = React.useState("");
    const [results, setResults] = React.useState<{ [eventid: string]: number }>({});
    const [loading, setLoading] = React.useState(false);
    const cancelRef = React.useRef(false);

    const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        // If value contains a comma, try to parse both lat and lng
        if (value.includes(",")) {
            const [latStr, lngStr] = value.split(",").map(s => s.trim());
            setLat(latStr);
            setLng(lngStr || "");
        } else {
            setLat(value);
        }
    };
    const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trim();
        // If value contains a comma, try to parse both lat and lng
        if (value.includes(",")) {
            const [latStr, lngStr] = value.split(",").map(s => s.trim());
            setLat(latStr || "");
            setLng(lngStr);
        } else {
            setLng(value);
        }
    };
    const handleSearch = async () => {
        setLoading(true);
        setResults({});
        cancelRef.current = false;
        const db = getDatabase();
        const eventsSnap = await get(ref(db, "/events"));
        const events = eventsSnap.val() || {};
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        console.log(events);
        const dst = 4; // 1 km
        let kpdata: KPDataType | undefined;
        for (const eventid of Object.keys(events)) {
            if (cancelRef.current) break;
            console.log("Checking event:", eventid);
            let count = 0;
            if (!events[eventid].arch) {
                const kpdataSnap = await get(ref(db, `/eventsdata/${eventid}/kpdata`));
                kpdata = kpdataSnap.val() || {};
                count = closeKPCount(kpdata, latNum, lngNum, dst);
            } else {
                const fdata = await getArchiveFile(eventid, events[eventid].arch, "evdata.json", true);
                if (!fdata) continue;
                kpdata = fdata.eventsdata.kpdata;
                console.log("Count from archive:", count);
            }
            if (!kpdata) continue;

            count = closeKPCount(kpdata, latNum, lngNum, dst);
            console.log("Event:", eventid, "Count:", count);
            if (count > 0) {
                setResults(prev => ({ ...prev, [eventid]: count }));
            }
        }
        setLoading(false);
    };

    const handleCancel = () => {
        console.log("Canceling search");
        cancelRef.current = true;
        setOpen(false);
        setLoading(false);
    };
    return <>
        <Button
            variant="contained"
            onClick={() => setOpen(true)}
        >
            Find events by location
        </Button>
        <Dialog open={open} onClose={() => setOpen(false)}>
            <DialogContent>
                <TextField
                    label="Latitude"
                    type="text"
                    value={lat}
                    onChange={handleLatChange}
                    fullWidth
                    margin="normal"
                    placeholder='e.g. 60.12345 or "60.12345, 24.54321"'
                />
                <TextField
                    label="Longitude"
                    type="text"
                    value={lng}
                    onChange={handleLngChange}
                    fullWidth
                    margin="normal"
                />
                {loading && <CircularProgress />}
                <List>
                    {Object.entries(results).map(([eventid, count]) => (
                        <ListItem key={eventid}>
                            <ListItemText primary={`Event: ${eventid}, Points: ${count}`} />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={handleSearch}>Search</Button>
            </DialogActions>
        </Dialog>
    </>
}